// use-entity-state — Thin React-Wrapper um den entity-state-store.
// V1-Pendant: src/hooks/useEntityState.js. V3-Refactor: alle State-Übergangs- und
// Berechnungslogik liegt im Store + lib/entity-state.ts. Der Hook startet zwei
// Tick-Intervalle (5s cognitive, 10s nodes), genau wie V1.

import { useEffect, useMemo } from 'react';
import { useEntityStateStore } from '@/stores/entity-state-store';
import type {
  EmotionalContext,
  EntityState,
  EntityStatusSummary,
  MarketEvent,
} from '@/types/eve-state';

export interface UseEntityStateOptions {
  /** Setzt initialen Autonomy-Level (V1-Default 65). */
  initialAutonomy?: number;
  /** Wenn `false`, werden die periodischen Ticks NICHT gestartet (Test-Hilfe). */
  enableTicks?: boolean;
}

export interface UseEntityStateReturn extends EntityStatusSummary {
  entityState: EntityState;
  emotionalContext: EmotionalContext;
  autonomyLevel: number;
  decisionConfidence: number;
  activeNodes: number;
  totalNodes: number;
  cognitiveLoad: number;
  stressLevel: number;
  focusLevel: number;
  transitionState: (next: EntityState) => boolean;
  adjustAutonomy: (level: number) => void;
  handleMarketEvent: (event: MarketEvent) => void;
  reset: () => void;
}

export function useEntityState(options: UseEntityStateOptions = {}): UseEntityStateReturn {
  const { initialAutonomy, enableTicks = true } = options;

  const entityState = useEntityStateStore((s) => s.entityState);
  const emotionalContext = useEntityStateStore((s) => s.emotionalContext);
  const autonomyLevel = useEntityStateStore((s) => s.autonomyLevel);
  const decisionConfidence = useEntityStateStore((s) => s.decisionConfidence);
  const activeNodes = useEntityStateStore((s) => s.activeNodes);
  const totalNodes = useEntityStateStore((s) => s.totalNodes);
  const cognitiveLoad = useEntityStateStore((s) => s.cognitiveLoad);
  const stressLevel = useEntityStateStore((s) => s.stressLevel);
  const focusLevel = useEntityStateStore((s) => s.focusLevel);

  const transitionState = useEntityStateStore((s) => s.transitionState);
  const adjustAutonomy = useEntityStateStore((s) => s.adjustAutonomy);
  const handleMarketEvent = useEntityStateStore((s) => s.handleMarketEvent);
  const applyCognitiveTick = useEntityStateStore((s) => s.applyCognitiveTick);
  const applyNodeTick = useEntityStateStore((s) => s.applyNodeTick);
  const reset = useEntityStateStore((s) => s.reset);

  // Setze Initial-Autonomy beim Mount falls vom Caller spezifiziert
  useEffect(() => {
    if (initialAutonomy !== undefined && initialAutonomy !== autonomyLevel) {
      adjustAutonomy(initialAutonomy);
    }
    // intentional: nur beim Mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 5s cognitive tick + 10s node tick (V1-Verhalten)
  useEffect(() => {
    if (!enableTicks) return undefined;
    const cogId = setInterval(applyCognitiveTick, 5000);
    const nodeId = setInterval(applyNodeTick, 10_000);
    return () => {
      clearInterval(cogId);
      clearInterval(nodeId);
    };
  }, [enableTicks, applyCognitiveTick, applyNodeTick]);

  const summary = useMemo<EntityStatusSummary>(
    () => useEntityStateStore.getState().getStatusSummary(),
    [
      entityState,
      emotionalContext,
      autonomyLevel,
      decisionConfidence,
      activeNodes,
      totalNodes,
      cognitiveLoad,
      stressLevel,
      focusLevel,
    ],
  );

  return {
    entityState,
    emotionalContext,
    autonomyLevel,
    decisionConfidence,
    activeNodes,
    totalNodes,
    cognitiveLoad,
    stressLevel,
    focusLevel,
    ...summary,
    transitionState,
    adjustAutonomy,
    handleMarketEvent,
    reset,
  };
}
